import React, { cloneElement } from 'react';

import AlertDialog from 'components/AlertDialog';
import createChainedFunction from 'utils/createChainedFunction';
import LoadingModal from 'components/LoadingModal';

// no idea why this is redundantly nested
type ErrorOrString = string | { error: string };
type RequestHideType = (() => any) | ((...args: any) => void) | undefined;

/**
 * Default error modal, when a modal without a defined
 * error modal handler (prefetchErrorModal) returned an
 * uncaught error during the prefetch promises resolution.
 *
 * @param {Error} a promise error
 * @return {AlertDialog}
 */
class DefaultErrorDialog extends React.Component<{
  error?: ErrorOrString;
  onRequestHide: RequestHideType;
}> {
  render() {
    return (
      <AlertDialog
        onRequestHide={this.props.onRequestHide}
        title="Oops, an error has occurred!"
        subtext={
          this.props.error && typeof this.props.error !== 'string' && this.props.error.error
            ? this.props.error.error
            : ''
        }
        buttonText="Close"
      />
    );
  }
}

type ModalPrefetcherProps = {
  modal: React.ReactElement<{
    onRequestHide: RequestHideType;
  }> & {
    type: {
      prefetch: [Function];
      prefetchErrorModal?: Function;
    };
  };
  onRequestHide: RequestHideType;
};

type State = {
  prefetched: boolean;
  prefetchError?: ErrorOrString;
};

/**
 * Modal display that prefetch data (via prefetch static prop), a loading indicator will
 * be mounted will the promises are being resolved. If an uncaught error
 * occurs during the promises modal.prefetchErrorModal will be mounted
 *
 * class SomeCoolModal extends React.Component {
 *   static prefetch = [
 *     (props) => props.fetchSomeCoolData(),
 *     (props) => props.fetchSomeFutherCoolData(),
 *   ]
 *
 *   static prefetchErrorModal = (err) => <AlertDialog title="Cannot get data" subtext={err} />
 * }
 *
 */
export default class ModalPrefetcher extends React.Component<ModalPrefetcherProps, State> {
  state: State = {
    prefetched: false,
  };

  componentDidMount() {
    if (this.getPrefetchFns().length > 0) {
      const promises = this.getPrefetchFns().map((prefetchFn: any) =>
        prefetchFn(this.props.modal.props)
      );

      Promise.all(promises).then(
        () => this.setState({ prefetched: true }),
        err => this.setState({ prefetchError: err || { error: '' } })
      );
    }
  }

  getPrefetchFns() {
    return this.props.modal.type.prefetch || [];
  }

  getErrorDialog(error: string | Object) {
    return (
      this.props.modal.type.prefetchErrorModal && this.props.modal.type.prefetchErrorModal(error)
    );
  }

  render() {
    if (this.state.prefetchError != null) {
      const errorView = this.getErrorDialog(this.state.prefetchError);

      if (errorView) {
        return cloneElement(errorView, {
          onRequestHide: createChainedFunction(
            errorView.props.onRequestHide,
            this.props.onRequestHide
          ),
        });
      }

      return (
        <DefaultErrorDialog
          error={this.state.prefetchError}
          onRequestHide={this.props.onRequestHide ? this.props.onRequestHide : () => {}}
        />
      );
    }

    if (this.getPrefetchFns().length > 0 && !this.state.prefetched) {
      return <LoadingModal />;
    }

    const chainedFunc = createChainedFunction(
      this.props.modal.props.onRequestHide,
      this.props.onRequestHide
    );

    return cloneElement(this.props.modal, {
      onRequestHide: chainedFunc ? chainedFunc : undefined,
    });
  }
}
